import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import {
  bookmarkInvalidations,
  bookmarkQueries,
} from "@circle-react/lib/queries/bookmarks";
import type { BookmarkType } from "@circle-react/types/Bookmarks";
import { flatten, isFunction } from "lodash";
import { useInfiniteQuery, useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import {
  reactQueryDelete,
  reactQueryPost,
} from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";

interface UseBookmarksProps {
  bookmarkId?: number | null;
  bookmarkType?: BookmarkType;
  disableFetch?: boolean;
  onSettled?: () => void;
  onSuccess?: (bookmarkId: number | null) => void;
  record?: { id: number; [key: string]: any };
}

interface DeleteBookmarkPayload {
  bookmark_type?: BookmarkType;
}

export const useBookmarks = ({
  bookmarkId,
  bookmarkType,
  record,
  onSuccess,
  onSettled,
  disableFetch = false,
}: UseBookmarksProps) => {
  const { currentCommunityMember } = usePunditUserContext();
  const toastr = useToast();
  const resetQuery = async () => {
    await bookmarkInvalidations.list(bookmarkType);
  };

  const { mutate: createBookmark, isPending: isCreatingBookmark } = useMutation(
    {
      mutationFn: () =>
        reactQueryPost(internalApi.bookmarks.create(), {
          bookmark: { bookmark_type: bookmarkType, record_id: record?.id },
        }),
      onSuccess: data => {
        if (isFunction(onSuccess)) {
          onSuccess(data.id);
        }
        toastr.success(t("bookmarks.saved_to_bookmarks"));
      },

      onError: () => {
        toastr.error(t("bookmarks.errors.create_failure"));
      },

      onSettled: onSettled,
    },
  );

  const { mutate: deleteBookmark, isPending: isDeletingBookmark } = useMutation(
    {
      mutationFn: () =>
        reactQueryDelete<void, DeleteBookmarkPayload>(
          internalApi.bookmarks.destroy({ id: bookmarkId }),
          {
            bookmark_type: bookmarkType,
          },
        ),
      onSuccess: () => {
        if (isFunction(onSuccess)) {
          onSuccess(null);
        }
        toastr.success(t("bookmarks.removed_from_bookmarks"), {
          duration: "short",
          shouldUseProgress: false,
        });
      },

      onError: () => {
        toastr.error(t("bookmarks.errors.delete_failure"));
      },

      onSettled: onSettled,
    },
  );

  const { data, error, isInitialLoading, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      ...bookmarkQueries.list(bookmarkType),
      enabled: !!currentCommunityMember,
      staleTime: disableFetch ? Infinity : 0,
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      meta: {
        errorMessage: t("bookmarks.errors.fetch_failure"),
      },
    });

  const getBookmarks = () =>
    flatten(data?.pages?.map(page => page.records) || []);

  return {
    createBookmark,
    isCreatingBookmark,
    deleteBookmark,
    isDeletingBookmark,
    getBookmarks,
    isLoading: isInitialLoading,
    error,
    fetchNextPage,
    hasNextPage,
    resetQuery,
  };
};
